import { useCallback, useEffect, useRef, useState } from "react";

import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { useMediaContext } from "../contexts/MediaContext";
import { usePrevious } from "../helpers/commonUtils";
import { isDebugEnabled } from "../helpers/debug";
import { setWindowProperties } from "../service/window";
import { usePlayerStore } from "../store/usePlayerStore";
import { AnalyticsConfig, Tracker } from "../types/analytics";

import { useAdobeCustomStates } from "./useAdobeCustomStates";
import {
  getStreamType,
  importMediaSDK,
  parseContext,
  resolveAppMeasurement
} from "./useAdobeMediaSDK-helpers";

interface MediaConfig {
  debugLogging?: boolean;
  playerName?: string;
  ssl?: boolean;
  trackingServer?: string;
}

const initAnalytics = async (playerName): Promise<AnalyticsConfig> => {
  const { default: ADB } = await importMediaSDK();

  const adbMediaApi = ADB.Media;
  const MediaConfig = ADB.MediaConfig;

  const mediaConfig: MediaConfig = new MediaConfig();
  mediaConfig.debugLogging = isDebugEnabled("analytics");
  mediaConfig.playerName = playerName;
  mediaConfig.ssl = true;
  mediaConfig.trackingServer = __ANALYTICS_TRACKING_SERVER;

  const getAppMeasurement = await resolveAppMeasurement();
  const appMeasurement = getAppMeasurement();
  const marketingCloudVisitorId =
    appMeasurement.visitor.getMarketingCloudVisitorID();

  adbMediaApi.configure(mediaConfig, appMeasurement);
  return { adbMediaApi, marketingCloudVisitorId };
};

const getAnalyticsConfig = playerName => {
  if (!window.ylePlayer.analyticsConfig) {
    setWindowProperties({
      analyticsConfig: initAnalytics(playerName)
    });
  }
  return window.ylePlayer.analyticsConfig;
};

const startsFromBeginning = (isLive: boolean, currentTime: number) =>
  !isLive && currentTime <= 0;

export const useAdobeMediaSDK = ({
  playerName: playerNameWithSuffix
}: {
  playerName: string;
}) => {
  const { getState, useStoreState } = usePlayerStore();
  const programId = useStoreState.programId();
  const isCasting = useStoreState.isCasting();

  const setAnalyticsEvents = useStoreState.setAnalyticsEvents();
  const setMarketingCloudVisitorId = useStoreState.setMarketingCloudVisitorId();

  const prevProgramId = usePrevious(programId);
  const prevIsCasting = usePrevious(isCasting);

  const [analyticsConfig, setAnalyticsConfig] = useState<AnalyticsConfig>();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [tracker, setTracker] = useState<Tracker | null>(null);

  // If the media is ondemand and starts from the beginning, no need to wait for initial seek
  // But if media is live or seek parameter is used and start time is greater than 0
  // we need to wait for the player has been seeked to the correct starting position
  // before starting analytics session
  const [initialSeekDone, setInitialSeekDone] = useState<boolean>(
    startsFromBeginning(getState().isLive, getState().currentTime)
  );

  const playheadBeforeSessionStart = useRef(0);

  const embedParameters = useEmbedParametersContext();
  const { dvrWindowInSeconds, title } = useMediaContext();
  useAdobeCustomStates(tracker, analyticsConfig?.adbMediaApi);

  // Remove build version placeholder if it has not been replaced in
  // GitHub workflow build step
  const playerName = playerNameWithSuffix.replace(/ __.*__/, "");

  const startNewSession = useCallback(() => {
    const state = getState();
    const currentProgramId = state.programId;

    if (!analyticsConfig || !currentProgramId) return;

    const { adbMediaApi } = analyticsConfig;

    const mediaObject = adbMediaApi.createMediaObject(
      title?.fin || title?.swe || "unknown",
      currentProgramId,
      state.durationInSeconds || 0,
      getStreamType(state.isLive, state.isAudio, adbMediaApi.StreamType),
      state.isAudio ? adbMediaApi.MediaType.Audio : adbMediaApi.MediaType.Video
    );

    if (state.isContinuingWatching) {
      mediaObject[adbMediaApi.MediaObjectKey.MediaResumed] = true;
    }

    const contextData = parseContext({
      dvrWindowInSeconds,
      embedParameters,
      isMobile: state.device.isMobile,
      programId: currentProgramId,
      state
    });

    const _tracker = adbMediaApi.getInstance();
    _tracker.trackSessionStart(mediaObject, contextData);
    _tracker.updatePlayhead(playheadBeforeSessionStart.current);
    _tracker.trackPlay();
    setTracker(_tracker);
  }, [analyticsConfig, dvrWindowInSeconds, embedParameters, getState, title]);

  const trackPlay = useCallback(() => {
    if (!getState().isCasting) {
      tracker?.trackPlay();
      setIsPlaying(true);
    }
  }, [getState, tracker]);

  const trackPause = useCallback(() => {
    if (!getState().isCasting) {
      tracker?.trackPause();
      setIsPlaying(false);
    }
  }, [getState, tracker]);

  const trackSeekStart = useCallback(() => {
    if (!getState().isCasting) {
      tracker?.trackEvent(analyticsConfig?.adbMediaApi.Event.SeekStart);
    }
  }, [analyticsConfig, getState, tracker]);

  const trackSeekComplete = useCallback(() => {
    if (!tracker) {
      setInitialSeekDone(true);
    }
    if (!getState().isCasting) {
      tracker?.trackEvent(analyticsConfig?.adbMediaApi.Event.SeekComplete);
    }
  }, [analyticsConfig?.adbMediaApi.Event.SeekComplete, getState, tracker]);

  const trackFinish = useCallback(() => {
    tracker?.trackComplete();
    tracker?.destroy();
    setTracker(null);
    setIsPlaying(false);
    setInitialSeekDone(
      startsFromBeginning(getState().isLive, getState().currentTime)
    );
  }, [getState, tracker]);

  const setTimeUpdate = useCallback(
    (currentTime: number) => {
      if (!getState().isCasting) {
        if (tracker) {
          tracker.updatePlayhead(currentTime);
        } else {
          playheadBeforeSessionStart.current = currentTime;
        }
      }
    },
    [getState, tracker]
  );

  useEffect(() => {
    if (embedParameters.analytics && __ANALYTICS_ENABLED) {
      getAnalyticsConfig(playerName).then(config => {
        setAnalyticsConfig(config);
        setMarketingCloudVisitorId(config.marketingCloudVisitorId);
      });
    }
  }, [embedParameters, playerName, setMarketingCloudVisitorId]);

  useEffect(() => {
    if (embedParameters.analytics && __ANALYTICS_ENABLED) {
      setAnalyticsEvents({
        onFinish: trackFinish,
        onPause: trackPause,
        onPlay: trackPlay,
        onSeekEnd: trackSeekComplete,
        onSeekStart: trackSeekStart,
        onTimeUpdate: setTimeUpdate
      });
    }
  }, [
    embedParameters,
    setAnalyticsEvents,
    setTimeUpdate,
    trackFinish,
    trackPause,
    trackPlay,
    trackSeekComplete,
    trackSeekStart
  ]);

  useEffect(() => {
    if (
      analyticsConfig &&
      isPlaying &&
      initialSeekDone &&
      !tracker &&
      programId
    ) {
      startNewSession();
    }
  }, [
    analyticsConfig,
    programId,
    isPlaying,
    startNewSession,
    tracker,
    initialSeekDone
  ]);

  useEffect(() => {
    if (isCasting) {
      tracker?.trackPause();
    } else if (!isCasting && prevIsCasting) {
      tracker?.updatePlayhead(getState().currentTime);
    }
  }, [getState, isCasting, prevIsCasting, tracker]);

  useEffect(() => {
    // On media change
    if (programId && prevProgramId && programId !== prevProgramId) {
      trackFinish();
    }
  }, [prevProgramId, programId, trackFinish]);
};
